import { BookOpen, Award, Users, Clock, Sparkles, TrendingUp, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { FeatureDetailDialog } from "@/components/landing/FeatureDetailDialog";
import { FeatureMiniMockup, type MiniMockupVariant } from "@/components/landing/FeatureMiniMockup";
import { useFeatureHoverPopup } from "@/components/landing/useFeatureHoverPopup";

const dashboardShowcase = "/dashboard-showcase.png";

type WhyKey =
  | "unlimitedAccess"
  | "silvaCoinBonus"
  | "prioritySupport"
  | "earlyAccess"
  | "certificates"
  | "analytics";

interface WhyItem {
  key: WhyKey;
  icon: typeof BookOpen;
  /** Component-mockup variant rendered inline + in popup hero (when no real screenshot). */
  mockup: MiniMockupVariant;
  /** Optional real screenshot — overrides the mockup inside the popup. */
  screenshot?: string;
  gradient: string;
  ctaHref?: string;
}

const items: WhyItem[] = [
  { key: "unlimitedAccess", icon: BookOpen, mockup: "navigation", gradient: "from-primary/15 to-accent/15", ctaHref: "/courses" },
  { key: "silvaCoinBonus", icon: Award, mockup: "streaks", gradient: "from-accent/20 to-primary/15", ctaHref: "/gamification" },
  { key: "prioritySupport", icon: Users, mockup: "insights", gradient: "from-secondary/20 to-primary/15", ctaHref: "/support" },
  { key: "earlyAccess", icon: Clock, mockup: "timeTracking", gradient: "from-primary/15 to-secondary/20", ctaHref: "/courses" },
  { key: "certificates", icon: Sparkles, mockup: "certificates", gradient: "from-accent/15 to-primary/20", ctaHref: "/dashboard" },
  { key: "analytics", icon: TrendingUp, mockup: "detailedAnalytics", screenshot: dashboardShowcase, gradient: "from-primary/15 to-accent/20", ctaHref: "/my-analytics" },
];

export const WhyChooseUs = () => {
  const { t } = useTranslation();
  const { open, setOpen, activeKey, handleSelect } = useFeatureHoverPopup<WhyKey>();

  const active = items.find((i) => i.key === activeKey) ?? null;
  const bullets = active
    ? (t(`featureDetails.why.${active.key}.bullets`, { returnObjects: true }) as unknown)
    : [];
  const bulletsArray = Array.isArray(bullets) ? (bullets as string[]) : [];

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-background via-card/20 to-background relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px] -z-10" />

      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 space-y-4 max-w-3xl mx-auto">
          <h2 className="heading-page font-serif italic">
            <span className="bg-gradient-primary bg-clip-text text-transparent leading-[1.2] inline-block pb-1">
              {t("whyChooseUs.title")}
            </span>
          </h2>
          <div className="divider-foil" />
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {t("whyChooseUs.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {items.map((item, index) => {
            const Icon = item.icon;
            const isActive = open && activeKey === item.key;
            return (
              <div
                key={item.key}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <button
                  type="button"
                  onClick={() => handleSelect(item.key)}
                  aria-label={`${t(`whyChooseUs.features.${item.key}.title`)} — ${t("common.viewDetails", "View details")}`}
                  className="w-full text-left h-full"
                >
                  <Card
                    className={`group relative h-full min-h-[340px] overflow-hidden transition-colors duration-300 ${
                      isActive ? "border-primary/50 shadow-glass-hover" : "hover:border-primary/40 hover:shadow-glass-hover"
                    }`}
                  >
                    {/* Mockup preview band */}
                    <div className={`relative h-28 w-full overflow-hidden border-b border-white/10 bg-gradient-to-br ${item.gradient}`}>
                      <FeatureMiniMockup variant={item.mockup} size="inline" />
                    </div>
                    <div
                      className={`absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-background/70 backdrop-blur-md border border-primary/30 flex items-center justify-center transition-opacity ${
                        isActive ? "opacity-100 bg-primary/20" : "opacity-80 group-hover:opacity-100 group-hover:bg-primary/20"
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <CardContent className="text-center space-y-3 p-5">
                      <div className="mx-auto w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shadow-glass">
                        <Icon className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                        {t(`whyChooseUs.features.${item.key}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t(`whyChooseUs.features.${item.key}.description`)}
                      </p>
                    </CardContent>
                  </Card>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {active && (
        <FeatureDetailDialog
          open={open}
          onOpenChange={setOpen}
          icon={active.icon}
          title={t(`whyChooseUs.features.${active.key}.title`)}
          shortDescription={t(`whyChooseUs.features.${active.key}.description`)}
          longDescription={t(`featureDetails.why.${active.key}.long`)}
          bullets={bulletsArray}
          bulletsTitle={t("featureDetails.whatYouCanDo")}
          screenshotSrc={active.screenshot}
          screenshotAlt={t(`whyChooseUs.features.${active.key}.title`)}
          customHero={<FeatureMiniMockup variant={active.mockup} />}
          gradient={active.gradient}
          ctaLabel={active.ctaHref ? t("featureDetails.tryItNow") : undefined}
          ctaHref={active.ctaHref}
        />
      )}
    </section>
  );
};
